import type { Router, RouteLocationNormalizedLoaded } from 'vue-router';
import {
  captureEmbedOrganizationContext,
  getEmbedNavigationHomeUrl,
  isEmbeddedContext,
  navigateToEmbedOrganizationHome,
  resolveEmbedOrganizationContext,
} from '@/utils/embeddedUtils';

export const CHECKOUT_RETURN_PATH_KEY = 'checkoutReturnPath';
export const CHECKOUT_RETURN_LABEL_KEY = 'checkoutReturnLabel';

const MARKETPLACE_PATH = '/web/dashboard';
const DEFAULT_MARKETPLACE_LABEL = 'Browse Marketplace';
const DEFAULT_HOME_LABEL = 'Back to Home';

/** Temporary post-checkout promo for San Diego Gives — disable when campaign ends. */
export const SAN_DIEGO_GIVES_SUCCESS_PROMO_ENABLED = true;
export const SAN_DIEGO_GIVES_ORG_PATH = '/web/org/sandiegogives';

export interface CheckoutReturnContext {
  path: string;
  label?: string;
}

function isCheckoutPath(path: string): boolean {
  return path.startsWith('/web/checkout')
    || path.startsWith('/web/ticket/checkout')
    || path.startsWith('/payment/intent/cart');
}

export function isSafeCheckoutReturnPath(path: string): boolean {
  // `//host` and `/\host` are other origins, not in-app paths
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return false;
  return !isCheckoutPath(path);
}

/** Whether `from` is a page to return to after checkout (not a reload, sign-in or payment hop). */
export function isCheckoutOrigin(
  from: { path?: string; matched?: unknown[] },
  checkoutPath: string,
): boolean {
  if (!from.path || !from.matched?.length || from.path === checkoutPath) return false;
  return !from.path.startsWith('/auth') && !from.path.startsWith('/payment');
}

export function saveCheckoutReturn(context: CheckoutReturnContext): void {
  if (!context.path || !isSafeCheckoutReturnPath(context.path)) return;

  sessionStorage.setItem(CHECKOUT_RETURN_PATH_KEY, context.path);
  if (context.label) {
    sessionStorage.setItem(CHECKOUT_RETURN_LABEL_KEY, context.label);
  } else {
    sessionStorage.removeItem(CHECKOUT_RETURN_LABEL_KEY);
  }
}

export function loadCheckoutReturn(): CheckoutReturnContext | null {
  const path = sessionStorage.getItem(CHECKOUT_RETURN_PATH_KEY);
  if (!path || !isSafeCheckoutReturnPath(path)) return null;

  const label = sessionStorage.getItem(CHECKOUT_RETURN_LABEL_KEY) || undefined;
  return { path, label };
}

export function clearCheckoutReturn(): void {
  sessionStorage.removeItem(CHECKOUT_RETURN_PATH_KEY);
  sessionStorage.removeItem(CHECKOUT_RETURN_LABEL_KEY);
}

function isSanDiegoGivesReturnPath(path: string): boolean {
  return path.includes('sandiegogives') || path === '/web/sandiegogives';
}

export function isSanDiegoGivesCheckoutReturn(
  route?: RouteLocationNormalizedLoaded,
): boolean {
  const saved = loadCheckoutReturn();
  if (saved?.path && isSanDiegoGivesReturnPath(saved.path)) {
    return true;
  }

  if (route) {
    const returnTo = typeof route.query.returnTo === 'string' ? route.query.returnTo : null;
    if (returnTo && isSanDiegoGivesReturnPath(returnTo)) {
      return true;
    }

    const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : null;
    if (redirect) {
      const redirectQuery = redirect.split('?')[1];
      if (redirectQuery) {
        const nestedReturnTo = new URLSearchParams(redirectQuery).get('returnTo');
        if (nestedReturnTo && isSanDiegoGivesReturnPath(nestedReturnTo)) {
          return true;
        }
      }
    }
  }

  return false;
}

/** Temporary SDG promo on guest complete-profile and ticket success pages. */
export function shouldShowSanDiegoGivesSuccessPromo(
  route?: RouteLocationNormalizedLoaded,
): boolean {
  if (!SAN_DIEGO_GIVES_SUCCESS_PROMO_ENABLED || !route) return false;

  if (route.path.startsWith('/auth/web/complete-profile')) {
    return true;
  }

  if (route.path.startsWith('/web/ticket/success')) {
    return true;
  }

  return isSanDiegoGivesCheckoutReturn(route);
}

export function getCheckoutReturnForRedirect(
  route?: RouteLocationNormalizedLoaded,
): CheckoutReturnContext | null {
  const saved = loadCheckoutReturn();
  if (saved) return saved;

  if (!route) return null;

  const returnTo = typeof route.query.returnTo === 'string' ? route.query.returnTo : null;
  const returnLabel = typeof route.query.returnLabel === 'string'
    ? route.query.returnLabel
    : undefined;

  if (returnTo && isSafeCheckoutReturnPath(returnTo)) {
    return { path: returnTo, label: returnLabel };
  }

  return null;
}

export function appendCheckoutReturnQuery(
  url: string,
  route?: RouteLocationNormalizedLoaded,
): string {
  const ctx = getCheckoutReturnForRedirect(route);
  if (!ctx?.path) return url;

  const separator = url.includes('?') ? '&' : '?';
  const params = new URLSearchParams();
  params.set('returnTo', ctx.path);
  if (ctx.label) {
    params.set('returnLabel', ctx.label);
  }
  return `${url}${separator}${params.toString()}`;
}

export function persistCheckoutReturnFromRoute(
  route: RouteLocationNormalizedLoaded,
  options?: { overwrite?: boolean },
): void {
  const returnTo = typeof route.query.returnTo === 'string' ? route.query.returnTo : null;
  const returnLabel = typeof route.query.returnLabel === 'string'
    ? route.query.returnLabel
    : undefined;

  if (returnTo && isSafeCheckoutReturnPath(returnTo)) {
    saveCheckoutReturn({ path: returnTo, label: returnLabel });
    return;
  }

  const existing = loadCheckoutReturn();
  if (returnLabel && existing?.path) {
    saveCheckoutReturn({ path: existing.path, label: returnLabel });
    return;
  }

  if (!options?.overwrite && existing) {
    return;
  }

  captureEmbedOrganizationContext(route);
  const embedHome = getEmbedNavigationHomeUrl(resolveEmbedOrganizationContext(route));
  if (embedHome && (isEmbeddedContext() || route.query.embedOrganizationId)) {
    saveCheckoutReturn({ path: embedHome, label: returnLabel || 'Shop' });
  }
}

export function resolveCheckoutReturnLabel(
  route?: RouteLocationNormalizedLoaded,
  options?: { forSuccess?: boolean },
): string {
  const saved = loadCheckoutReturn();
  if (saved?.label) {
    return `Back to ${saved.label}`;
  }

  if (route) {
    const embedContext = resolveEmbedOrganizationContext(route);
    if (embedContext.organizationId && (isEmbeddedContext() || route.query.embedOrganizationId)) {
      return 'Back to Shop';
    }
  }

  return options?.forSuccess ? DEFAULT_HOME_LABEL : DEFAULT_MARKETPLACE_LABEL;
}

export function navigateCheckoutReturn(
  router: Router,
  route?: RouteLocationNormalizedLoaded,
  options?: { fallbackPath?: string },
): void {
  const fallback = options?.fallbackPath || MARKETPLACE_PATH;
  const saved = loadCheckoutReturn();

  if (saved?.path) {
    clearCheckoutReturn();
    router.push(saved.path);
    return;
  }

  if (route) {
    const returnTo = typeof route.query.returnTo === 'string' ? route.query.returnTo : null;
    if (returnTo && isSafeCheckoutReturnPath(returnTo)) {
      clearCheckoutReturn();
      router.push(returnTo);
      return;
    }
  }

  if (route && isEmbeddedContext()) {
    const embedContext = resolveEmbedOrganizationContext(route);
    const navigated = navigateToEmbedOrganizationHome(route, {
      organizationId: embedContext.organizationId || undefined,
      embedSource: embedContext.embedSource || undefined,
      stickerId: embedContext.stickerId || undefined,
      standalone: embedContext.standalone,
    }, { router });

    if (navigated) {
      clearCheckoutReturn();
      return;
    }
  }

  if (route) {
    const embedHome = getEmbedNavigationHomeUrl(resolveEmbedOrganizationContext(route));
    if (embedHome && (isEmbeddedContext() || route.query.embedOrganizationId)) {
      clearCheckoutReturn();
      router.push(embedHome);
      return;
    }
  }

  const hasSameOriginReferrer = typeof document !== 'undefined'
    && document.referrer
    && document.referrer.startsWith(window.location.origin)
    && !document.referrer.includes('/web/checkout');

  if (hasSameOriginReferrer) {
    clearCheckoutReturn();
    router.back();
    return;
  }

  clearCheckoutReturn();
  router.push(fallback);
}

export function buildCheckoutRouteQuery(
  route: RouteLocationNormalizedLoaded,
  extra: Record<string, string> = {},
): Record<string, string> {
  const query: Record<string, string> = { ...extra };

  Object.entries(route.query).forEach(([key, value]) => {
    if (value && !query[key]) {
      query[key] = String(value);
    }
  });

  if (route.path.includes('/embed/') && !query.embedOrganizationId) {
    const orgPathMatch = route.path.match(/\/embed\/organization\/([^/?]+)/);
    if (orgPathMatch) {
      query.embedOrganizationId = orgPathMatch[1];
    }
  }

  return query;
}

export function navigateToCheckout(
  router: Router,
  route: RouteLocationNormalizedLoaded,
  options?: {
    returnTo?: string;
    returnLabel?: string;
    query?: Record<string, string>;
  },
): void {
  const returnTo = options?.returnTo
    ?? (isCheckoutPath(route.path) ? undefined : route.fullPath);

  if (returnTo && isSafeCheckoutReturnPath(returnTo)) {
    saveCheckoutReturn({
      path: returnTo,
      label: options?.returnLabel,
    });
  }

  const query = buildCheckoutRouteQuery(route, {
    ...options?.query,
    ...(returnTo && isSafeCheckoutReturnPath(returnTo) ? { returnTo } : {}),
    ...(options?.returnLabel ? { returnLabel: options.returnLabel } : {}),
  });

  router.push({ path: '/web/checkout', query });
}
